// import { cleanAudioAdaptive, cleanAudioRNNoise } from "./audioRNNoise";

import { getMeetingMedia, lockFrontCamera } from "../../utils/media";
import { cleanAudioRNNoise } from "../../utils/rnnoise";
import { mediasoupClientConfig } from "./mediasoup";

const isSystemAliasDevice = (deviceId: string) =>
  deviceId === "default" || deviceId === "communications";

const normalizeDeviceLabel = (label: string) =>
  label.replace(/^(Default|Communications)\s*-\s*/i, "").trim();

const getUniqueMediaInputs = (devices: MediaDeviceInfo[]) => {
  const unique = new Map<string, MediaDeviceInfo>();

  devices.forEach((device) => {
    const key =
      device.groupId ||
      normalizeDeviceLabel(device.label).toLowerCase() ||
      device.deviceId;
    const existing = unique.get(key);

    if (!existing) {
      unique.set(key, device);
      return;
    }

    if (
      isSystemAliasDevice(existing.deviceId) &&
      !isSystemAliasDevice(device.deviceId)
    ) {
      unique.set(key, device);
    }
  });

  return Array.from(unique.values());
};

const getFallbackInputId = async (kind: MediaDeviceKind) => {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return (
    getUniqueMediaInputs(devices.filter((device) => device.kind === kind)).at(0)
      ?.deviceId || ""
  );
};

const buildAudioConstraints = (deviceId?: string): MediaTrackConstraints => ({
  ...mediasoupClientConfig.audioConstraints,
  deviceId: deviceId ? { exact: deviceId } : undefined,
});

const buildVideoConstraints = (deviceId?: string): MediaTrackConstraints => ({
  width: { ideal: 640 },
  height: { ideal: 360 },
  frameRate: { ideal: 15, max: 15 },
  deviceId: deviceId ? { exact: deviceId } : undefined,
});

const createRnnoiseStream = async (
  rawStream: MediaStream,
  audioDeviceId?: string,
  videoDeviceId?: string,
) => {
  const rawAudioTrack = rawStream.getAudioTracks()[0];
  if (!rawAudioTrack) {
    const finalStream = new MediaStream([...rawStream.getVideoTracks()]);

    (finalStream as any).__videoDeviceId =
      videoDeviceId ||
      rawStream.getVideoTracks()[0]?.getSettings().deviceId ||
      (await getFallbackInputId("videoinput"));

    return finalStream;
  }

  if (rawAudioTrack?.applyConstraints) {
    await rawAudioTrack
      .applyConstraints(buildAudioConstraints(audioDeviceId))
      .catch((error) => {
        console.warn("Failed to apply echo cancellation constraints.", error);
      });
  }

  const cleanAudioStream = await cleanAudioRNNoise(rawStream);

  const finalStream = new MediaStream([
    ...cleanAudioStream.getAudioTracks(),
    ...rawStream.getVideoTracks(),
  ]);

  (finalStream as any).__audioDeviceId =
    audioDeviceId ||
    rawStream.getAudioTracks()[0]?.getSettings().deviceId ||
    (await getFallbackInputId("audioinput"));
  (finalStream as any).__videoDeviceId =
    videoDeviceId ||
    rawStream.getVideoTracks()[0]?.getSettings().deviceId ||
    (await getFallbackInputId("videoinput"));

  return finalStream;
};

export async function getRnnoiseAudioStream(deviceId?: string) {
  const rawStream = await getMeetingMedia({
    audio: buildAudioConstraints(deviceId),
    video: false,
  });

  return createRnnoiseStream(rawStream, deviceId);
}

export async function getUserMedia() {
  let rawStream: MediaStream;

  try {
    rawStream = await getMeetingMedia({
      video: lockFrontCamera(mediasoupClientConfig.videoConstraints),
    });
  } catch {
    rawStream = await getMeetingMedia({
      video: lockFrontCamera(buildVideoConstraints()),
      audio: false,
    });
  }

  return createRnnoiseStream(rawStream);
}

// get multiple video audios
// const roleNames = ["Judge", "Prosecutor", "Defence", "Witness"];

export async function getAllHostMediaStreams() {
  const permissionStream = await getMeetingMedia({ audio: true, video: true });

  permissionStream.getTracks().forEach((t) => t.stop());

  const devices = await navigator.mediaDevices.enumerateDevices();

  const videoInputs = getUniqueMediaInputs(
    devices.filter((d) => d.kind === "videoinput"),
  );
  const audioInputs = getUniqueMediaInputs(
    devices.filter((d) => d.kind === "audioinput"),
  );

  const streams: MediaStream[] = [];

  const pairCount = Math.min(videoInputs.length, audioInputs.length);
  console.log("pairCount", pairCount);

  for (let i = 0; i < pairCount; i++) {
    try {
      const rawStream = await getMeetingMedia({
        audio: buildAudioConstraints(audioInputs[i].deviceId),
        video: buildVideoConstraints(videoInputs[i].deviceId),
      });
      const finalStream = await createRnnoiseStream(
        rawStream,
        audioInputs[i].deviceId,
        videoInputs[i].deviceId,
      );

      streams.push(finalStream);
    } catch (err) {
      console.error(`Failed for device index ${i}`, err);
    }

    // avoid device driver crash
    await new Promise((r) => setTimeout(r, 400));
  }

  return streams;
}

export async function getScreenMedia() {
  return navigator.mediaDevices.getDisplayMedia({
    video: true,
  });
}
